import { ToastOptionsSuccess, type ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiService, useApi, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import {
  getFileExtension,
  type DocumentNew,
  type DocumentRelatedEntity,
  type FileUpload,
  type FileUploadResponse,
} from "@modernary/mui/components/document";
import { apiHosts } from "@/api/constants/apiHosts";
import type { MuiFile, MuiFileUpload } from "@modernary/mui/utils/fk";
import type { LE } from "@/constants/enums";

const URL = "FileUpload";
const ENTITY_NAME = "File";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<FileUpload>(apiHosts.AQMS, axiosParams);

/**
 * @param {String} keywords Matches against first name, last name, email
 * @param {abortCallback} abortMethod Callback for when a request has been aborted
 */
// const getFileUploads = (keywords, abortMethod, config: PagingApiAndNotifierConfig = {}) => {
//   return api.get(URL, {
//     params: { keywords },
//     abort: config?.abortMethod,
//     successConfig: { hide: true },
//     errorConfig: { hide: hideNotifications, fetched: ENTITY_NAME },
//   });
// };

/**
 * @param {Object} params Query parameters, varies (TODO fix)
 * @param {abortCallback} abortMethod Callback for when a request has been aborted
 */
const getFileUploads = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.get(url, {
    params,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {Int} id
 */
const getFileUploadById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewFileUpload = async () => {
  //optionally make any other changes as necessary before returning.
  return { dataRef: {} };
};

const createFileUpload = (fileUpload: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const data = new FormData();
  fileUpload.files.forEach((file: any) => data.append("files", file.file));

  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, data, {
    onUploadProgress: function (progressEvent: ProgressEvent) {
      // Do whatever you want with the native progress event
      // console.log("progress", progressEvent);
    },
    headers: {
      "Content-Type": "multipart/form-data",
    },
    successToastCb,
    errorToastCb,
  });
};

const updateFileUpload = (fileUpload: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL, fileUpload, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {FileUpload} fileUpload This cannot be a 'ref' to a fileUpload
 */
const saveFileUpload = (fileUpload: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  if (fileUpload.id) {
    return updateFileUpload(fileUpload, config);
  } else {
    return createFileUpload(fileUpload, config);
  }
};

const deleteFileUploadById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * Uploads files. By default, it's expected that the `muiFile` input type is in use; this must be used to get the additional properties from the FormKit node. (categoryMurn, typeMurn, displayName)
 * @param files The files to upload.
 * @param config.categoryMurn Overrides the category of the file.
 * @param config.typeMurn Overrides the type of the file.
 * @param config.displayName Overrides the display name of the file.
 * @param config.relatedEntityMurns List of murns for related entities for the given documents
 * @returns The document requests for the uploaded files. These can then be added to a documentRequests array.
 */
async function uploadDocuments(
  files: Array<MuiFile<LE.DocumentCategory, LE.DocumentType>>,
  config?: {
    categoryMurn?: LE.DocumentCategory;
    typeMurn?: LE.DocumentType;
    displayName?: string;
    relatedEntityMurns?: DocumentRelatedEntity[];
  },
) {
  // saveFileUpload
  const { exec: execSaveFileUpload } = useApi<MuiFileUpload<LE.DocumentCategory, LE.DocumentType>, FileUploadResponse>(
    saveFileUpload,
  );

  const newFiles = files.filter((file) => file.file);
  if (newFiles.length === 0) return [];

  const uploadedDocs = await execSaveFileUpload({ files: newFiles });
  if (!uploadedDocs) {
    throw new Error("File was not uploaded.");
  }

  const result = uploadedDocs.map((doc, index) => {
    if (doc.hasError) {
      throw new Error("File was not uploaded.");
    }

    let documentToSave: DocumentNew = {};

    const fileInputData = newFiles[index];
    fileInputData.fileUploadId = doc.fileUploadId;
    delete fileInputData.file; // Remove the file data so it's not stored in the form JSON.

    //add in fields the user does not set
    documentToSave.fileUploadId = doc.fileUploadId;
    documentToSave.fileSize = doc.size;
    documentToSave.fileName = doc.name;
    documentToSave.contentType = doc.type;

    documentToSave.displayName = config?.displayName ?? fileInputData.displayName;
    documentToSave.categoryMurn = config?.categoryMurn ?? fileInputData.categoryMurn;
    documentToSave.typeMurn = config?.typeMurn ?? fileInputData.typeMurn;

    documentToSave.documentRelatedEntities = config?.relatedEntityMurns ?? [];

    return documentToSave;
  });

  return result;
}

function removeExtensionFromFileName(fileName: string | undefined) {
  if (fileName) {
    const ext = getFileExtension(fileName, false);
    if (fileName.endsWith(ext)) {
      return fileName.slice(0, -ext.length);
    }
  }

  return fileName;
}

// export interface InsightFile extends MuiFile<LE.DocumentCategory, LE.DocumentType> {}

export {
  deleteFileUploadById,
  getFileUploads,
  getFileUploadById,
  getNewFileUpload,
  createFileUpload,
  updateFileUpload,
  saveFileUpload,
  uploadDocuments,
};
